import { useMemo } from 'react';

import dayjs from 'dayjs';
import { LayoutCard, StatusChipV2, Box, Stack } from '@cast/design-system';
import {
  useClusterDashboardOomKillsData,
  useClusterDashboardServiceHealthData,
  useClusterDashboardState,
} from '@cast/features-cluster-shared';
import {
  deriveServiceHealthEmptyReason,
  lockServiceHealthRedMetrics,
  SERVICE_HEALTH_METRIC_ORDER,
} from '@cast/features-cluster-shared-utils';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { useReportCapabilitiesQuery } from '@cast/shared-cost-data-access';
import {
  useCastctlOnboardingDialog,
  useOnboardClusterDialog,
} from '@cast/split-me/hooks/dialogs';

import { DashboardSectionError } from './shared/DashboardSectionError';
import {
  ServiceHealthAgentCtaTile,
  ServiceHealthCtaTile,
} from './shared/ServiceHealthAgentCtaTile';
import { ServiceHealthTile } from './shared/ServiceHealthTile';
import { ServiceHealthTileSkeleton } from './shared/ServiceHealthTileSkeleton';

// Wired metrics only — see SERVICE_HEALTH_METRIC_ORDER.
const SERVICE_HEALTH_TILE_COUNT = SERVICE_HEALTH_METRIC_ORDER.length;

export const ServiceHealthSection = () => {
  const { cluster } = useCluster();
  const { showAgentInstallBanner } = useClusterDashboardState();
  const { openOnboardClusterDialog } = useOnboardClusterDialog();
  const { openCastctlOnboardingDialog } = useCastctlOnboardingDialog();
  const { isLoading: isOomLoading } = useClusterDashboardOomKillsData();
  const { metrics, isLoading, hasError, refetch } =
    useClusterDashboardServiceHealthData();

  const startOfCurrentHour = dayjs().startOf('hour');
  const {
    data: reportCapabilities,
    isLoading: isCapabilitiesLoading,
    error: capabilitiesError,
    refetch: refetchCapabilities,
  } = useReportCapabilitiesQuery({
    clusterId: cluster.id,
    startTime: startOfCurrentHour.toISOString(),
    endTime: startOfCurrentHour.endOf('hour').toISOString(),
    enabled: !!cluster.id && !showAgentInstallBanner,
  });

  const hasCapabilitiesError =
    !showAgentInstallBanner && !!capabilitiesError && !reportCapabilities;

  const reliabilityMetricsDataAvailable = isCapabilitiesLoading
    ? undefined
    : reportCapabilities?.reliabilityMetricsDataAvailable;

  const emptyReason = useMemo(
    () =>
      deriveServiceHealthEmptyReason({
        showAgentInstallBanner,
        reliabilityMetricsDataAvailable,
        metrics,
      }),
    [showAgentInstallBanner, reliabilityMetricsDataAvailable, metrics]
  );

  const displayMetrics = useMemo(() => {
    if (
      emptyReason === 'agent_missing' ||
      emptyReason === 'enable_reliability_metrics'
    ) {
      return lockServiceHealthRedMetrics(metrics);
    }

    return metrics;
  }, [emptyReason, metrics]);

  // Chips only reflect visible (non-locked) tiles.
  const breachCount = displayMetrics.filter(
    (metric) => metric.status === 'breached'
  ).length;
  const riskCount = displayMetrics.filter(
    (metric) => metric.status === 'at_risk'
  ).length;

  const headerChips =
    breachCount > 0 || riskCount > 0 ? (
      <Stack direction="row" alignItems="center" gap={8}>
        {breachCount > 0 ? (
          <StatusChipV2
            label={`${breachCount} unhealthy`}
            color="error"
            size="small"
            variant="subtle"
          />
        ) : null}
        {riskCount > 0 ? (
          <StatusChipV2
            label={`${riskCount} at risk`}
            color="warning"
            size="small"
            variant="subtle"
          />
        ) : null}
      </Stack>
    ) : null;

  const handleEnableReliabilityMetrics = () => {
    openCastctlOnboardingDialog({
      skipConnectionMonitoring: true,
      initialFeatureSelection: { reliabilityMetrics: true },
    });
  };

  const handleRetry = () => {
    void refetch();
    if (!showAgentInstallBanner) {
      void refetchCapabilities();
    }
  };

  return (
    <LayoutCard
      title="Cluster reliability"
      headerEndContent={headerChips}
      testId="service-health-section"
    >
      <DashboardSectionError
        hasError={hasError || hasCapabilitiesError}
        onRetry={handleRetry}
      >
        <Box
          display="grid"
          gridTemplateColumns="repeat(4, minmax(0, 1fr))"
          gap={12}
          sx={{
            '& > *': { minWidth: 0 },
            '@media (max-width: 1200px)': {
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            },
          }}
        >
          {isLoading || isOomLoading ? (
            Array.from({ length: SERVICE_HEALTH_TILE_COUNT }).map(
              (_, index) => <ServiceHealthTileSkeleton key={index} />
            )
          ) : (
            <>
              {displayMetrics.map((metric) => (
                <ServiceHealthTile key={metric.id} metric={metric} />
              ))}
              {emptyReason === 'agent_missing' ? (
                <ServiceHealthAgentCtaTile
                  onInstallAgent={openOnboardClusterDialog}
                />
              ) : null}
              {emptyReason === 'enable_reliability_metrics' ? (
                <ServiceHealthCtaTile
                  message="Track latency, error rate, and more with reliability metrics collection"
                  actionLabel="Enable reliability metrics"
                  onAction={handleEnableReliabilityMetrics}
                  actionTestId="service-health-enable-reliability-metrics-button"
                />
              ) : null}
              {emptyReason === 'no_data' ? (
                <ServiceHealthCtaTile message="No health data for this time range" />
              ) : null}
            </>
          )}
        </Box>
      </DashboardSectionError>
    </LayoutCard>
  );
};
